import React, { useCallback, useEffect, useState } from 'react';
import {
  catalogTasksList,
  catalogTasksUpsert,
  catalogTasksSoftDelete,
  catalogTranslateTask,
  catalogRoomsList,
  catalogMarkTaskReviewed,
  catalogToolsList,
  signUpload,
  putToSignedUrl,
  type CatalogTaskGroupRow,
  type CatalogTaskTemplateRow,
  type AgeGroupCode,
} from '../services/apiService';
import { supported_languages } from '../i18n/translations';
import { useGlobalAdminOutletContext } from './GlobalAdminLayout';
import RepeatSettings from './admin/RepeatSettings';
import StepTimerInput from './admin/StepTimerInput';
import { moveItem } from '../utils/reorder';

const SUGGESTED_ROOMS: { value: string; label: string }[] = [
  { value: '', label: '— No default room —' },
  { value: 'Kitchen', label: 'Kitchen' },
  { value: 'Living Room', label: 'Living Room' },
  { value: 'Parents Bedroom', label: 'Parents Bedroom' },
  { value: 'Child Bedroom', label: 'Child Bedroom' },
  { value: 'Bathroom', label: 'Bathroom' },
  { value: 'Washing Room', label: 'Washing Room' },
];

type EditForm = {
  template_id?: string;
  group_id?: string;
  group_key: string;
  sort_order: number;
  language_code: string;
  title: string;
  description: string;
  points: number;
  age_group: AgeGroupCode;
  checklist: Array<{ id: string; text: string; image_url?: string; timer_seconds?: number | null }>;
  tool_keys: string[];
  suggested_room_original_name: string;
  recurrence_days: number;
  recreate_on_validation: boolean;
  days_to_complete: number | '';
  auto_available: boolean;
  /** Fixed plan without a start date (it starts the day a family uses the task), 2026-10-15. */
  repeat_schedule: any;
  /** New family setup: a personal task in each child's room, or an open task for a chosen room. */
  starter: '' | 'per_child' | 'shared';
  starter_checked: boolean;
  /** HTTPS URL to a hosted image (same family app can show it via tasks.header_image_url) */
  header_image_url: string;
  needs_review: boolean;
};

const newChecklistId = () => `cl_${Math.random().toString(36).slice(2, 11)}`;

const emptyForm = (): EditForm => ({
  group_key: '',
  sort_order: 0,
  language_code: 'en',
  title: '',
  description: '',
  points: 5,
  age_group: 'all_ages',
  checklist: [{ id: newChecklistId(), text: '' }],
  tool_keys: [],
  suggested_room_original_name: '',
  recurrence_days: 0,
  recreate_on_validation: false,
  days_to_complete: '',
  auto_available: true,
  repeat_schedule: null,
  starter: '',
  starter_checked: false,
  header_image_url: '',
  needs_review: false,
});

function parseChecklist(t: CatalogTaskTemplateRow | undefined): Array<{ id: string; text: string; image_url?: string; timer_seconds?: number | null }> {
  if (!t) return [{ id: newChecklistId(), text: '' }];
  const raw = t.checklist_items;
  let arr: Array<{ text: string; order_index?: number; image_url?: string | null; timer_seconds?: number | null }> = [];
  if (Array.isArray(raw)) arr = raw as Array<{ text: string; order_index?: number; image_url?: string | null; timer_seconds?: number | null }>;
  else if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      arr = Array.isArray(p) ? p : [];
    } catch {
      arr = [];
    }
  }
  if (!arr.length) return [{ id: newChecklistId(), text: '' }];
  return arr.map((c) => {
    const row: { id: string; text: string; image_url?: string; timer_seconds?: number | null } = { id: newChecklistId(), text: c.text || '' };
    if (c.image_url != null && String(c.image_url).trim() !== '') row.image_url = String(c.image_url).trim();
    if (typeof c.timer_seconds === 'number' && c.timer_seconds > 0) row.timer_seconds = c.timer_seconds;
    return row;
  });
}

const GlobalCatalogTasks: React.FC = () => {
  const { refreshAnthropicUsage } = useGlobalAdminOutletContext();
  const [groups, setGroups] = useState<CatalogTaskGroupRow[]>([]);
  const [catalogToolKeys, setCatalogToolKeys] = useState<Array<{ tool_key: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  // "Suggested room" choices come from Master Admin's standard rooms (2026-10-15), so a new room can
  // get standard tasks; the old fixed list stays as a fallback.
  const [catalogRooms, setCatalogRooms] = useState<Array<{ task_room: string | null; names: string[] }>>([]);
  useEffect(() => {
    catalogRoomsList().then((gs) => setCatalogRooms(gs.map((g) => ({
      task_room: g.task_room,
      names: g.room_templates.filter((t) => t.language_code === 'en').map((t) => t.name),
    })))).catch(() => undefined);
  }, []);
  const roomOptions = (() => {
    const byValue = new Map<string, string[]>();
    for (const r of SUGGESTED_ROOMS) if (r.value) byValue.set(r.value, []);
    for (const r of catalogRooms) if (r.task_room) byValue.set(r.task_room, [...(byValue.get(r.task_room) || []), ...r.names]);
    return [SUGGESTED_ROOMS[0], ...Array.from(byValue.entries()).sort().map(([value, names]) => {
      const others = names.filter((n) => n !== value);
      return { value, label: others.length ? `${value} (${others.join(', ')})` : value };
    })];
  })();
  // Bulk: translate every active task that is missing languages (one task at a time).
  const [bulk, setBulk] = useState<{ done: number; total: number; failed: string[] } | null>(null);
  const bulkStop = React.useRef(false);
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);
  const [editing, setEditing] = useState<EditForm | null>(null);
  const [pendingHeaderFile, setPendingHeaderFile] = useState<File | null>(null);
  const [pendingChecklistFiles, setPendingChecklistFiles] = useState<Record<string, File>>({});
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState<string | null>(null);
  const [checklistPreviewUrls, setChecklistPreviewUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!pendingHeaderFile) {
      setHeaderPreviewUrl(null);
      return;
    }
    const u = URL.createObjectURL(pendingHeaderFile);
    setHeaderPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [pendingHeaderFile]);

  useEffect(() => {
    const urls: Record<string, string> = {};
    for (const [id, file] of Object.entries(pendingChecklistFiles)) {
      urls[id] = URL.createObjectURL(file);
    }
    setChecklistPreviewUrls(urls);
    return () => {
      for (const u of Object.values(urls)) URL.revokeObjectURL(u);
    };
  }, [pendingChecklistFiles]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [g, tools] = await Promise.all([catalogTasksList(), catalogToolsList()]);
      setGroups(g);
      setCatalogToolKeys(
        (tools || [])
          .filter((t) => t.is_active !== false)
          .map((t) => ({ tool_key: t.tool_key, name: t.name }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (e: any) {
      setErr(e?.message || 'Failed to load standard tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setPendingHeaderFile(null);
    setPendingChecklistFiles({});
    setEditing(emptyForm());
  };

  const mergeTemplate = (group: CatalogTaskGroupRow, lang: string, tpl?: CatalogTaskTemplateRow): EditForm => {
    const t = tpl || group.task_templates?.find((x) => x.language_code === lang);
    const base = emptyForm();
    return {
      ...base,
      template_id: t?.id,
      group_id: group.id,
      group_key: group.group_key || '',
      sort_order: group.sort_order ?? 0,
      language_code: lang,
      title: t?.title || '',
      description: t?.description || '',
      points: t?.points ?? 5,
      age_group: (t?.age_group as AgeGroupCode) || 'all_ages',
      checklist: parseChecklist(t),
      tool_keys: t && Array.isArray(t.tool_keys) ? [...t.tool_keys] : [],
      suggested_room_original_name: t?.suggested_room_original_name || '',
      recurrence_days: t?.recurrence_days ?? 0,
      recreate_on_validation: !!t?.recreate_on_validation,
      days_to_complete: t?.days_to_complete ?? '',
      auto_available: t?.auto_available !== false,
      repeat_schedule: (t as any)?.repeat_schedule ?? null,
      starter: ((group as any)?.starter as any) || '',
      starter_checked: !!(group as any)?.starter_checked,
      header_image_url: t?.header_image_url ? String(t.header_image_url) : '',
      needs_review: !!t?.needs_review,
    };
  };

  const openEdit = (group: CatalogTaskGroupRow) => {
    setPendingHeaderFile(null);
    setPendingChecklistFiles({});
    const templates = Array.isArray(group.task_templates) ? group.task_templates : [];
    const en = templates.find((x) => x.language_code === 'en');
    const first = templates[0];
    const lang = en?.language_code || first?.language_code || 'en';
    setEditing(mergeTemplate(group, lang, en || first));
  };

  const onLanguageChange = (group: CatalogTaskGroupRow, lang: string) => {
    setPendingHeaderFile(null);
    setPendingChecklistFiles({});
    const t = group.task_templates?.find((x) => x.language_code === lang);
    setEditing(mergeTemplate(group, lang, t));
  };

  const save = async () => {
    if (!editing) return;
    const validCheck = editing.checklist.map((c) => c.text.trim()).filter(Boolean);
    if (validCheck.length === 0) {
      setErr('Add at least one checklist item with text.');
      return;
    }
    if (!editing.title.trim()) {
      setErr('Title is required.');
      return;
    }
    if (!editing.group_id && !editing.group_key.trim()) {
      setErr('Task key (slug) is required for a new standard task.');
      return;
    }

    setSaving(true);
    setErr('');
    try {
      const validRows = editing.checklist.filter((c) => c.text.trim());
      const checklist_items: Array<{ text: string; order_index: number; image_url?: string | null; timer_seconds?: number }> = validRows.map(
        (c, i) => {
          const row: { text: string; order_index: number; image_url?: string | null; timer_seconds?: number } = {
            text: c.text.trim(),
            order_index: i + 1,
          };
          if (c.image_url) row.image_url = c.image_url;
          if (c.timer_seconds) row.timer_seconds = c.timer_seconds;
          return row;
        },
      );

      let header_image_url = editing.header_image_url.trim() || null;

      const shared = {
        template_id: editing.template_id,
        group_id: editing.group_id,
        group_key: editing.group_key.trim() || undefined,
        sort_order: editing.sort_order,
        language_code: editing.language_code,
        title: editing.title.trim(),
        description: editing.description.trim() || null,
        points: editing.points,
        age_group: editing.age_group,
        checklist_items,
        tool_keys: editing.tool_keys,
        suggested_room_original_name: editing.suggested_room_original_name || null,
        recurrence_days: editing.recurrence_days > 0 ? editing.recurrence_days : null,
        recreate_on_validation: editing.recreate_on_validation,
        days_to_complete: editing.days_to_complete === '' ? null : Number(editing.days_to_complete),
        auto_available: editing.auto_available,
        repeat_schedule: editing.recreate_on_validation ? editing.repeat_schedule : null,
        starter: editing.starter || null,
        starter_checked: editing.starter_checked,
        header_image_url,
        is_active: true,
      };

      let { template, group_id } = await catalogTasksUpsert(shared);

      const tid = template.id;
      const nextChecklist = checklist_items.map((row) => ({ ...row }));
      let nextHeader = header_image_url;

      if (pendingHeaderFile) {
        const ct = pendingHeaderFile.type || 'image/png';
        const signed = await signUpload({
          intent: 'catalog_task_header',
          content_type: ct,
          template_id: tid,
        });
        await putToSignedUrl(signed.upload_url, signed.headers, pendingHeaderFile);
        nextHeader = signed.public_url;
      }

      for (let i = 0; i < validRows.length; i++) {
        const c = validRows[i];
        const f = pendingChecklistFiles[c.id];
        if (!f) continue;
        const ct = f.type || 'image/png';
        const signed = await signUpload({
          intent: 'catalog_task_checklist_image',
          content_type: ct,
          template_id: tid,
          checklist_slot: String(i),
        });
        await putToSignedUrl(signed.upload_url, signed.headers, f);
        nextChecklist[i] = { ...nextChecklist[i], image_url: signed.public_url };
      }

      if (pendingHeaderFile || Object.keys(pendingChecklistFiles).length > 0) {
        await catalogTasksUpsert({
          ...shared,
          template_id: tid,
          group_id,
          checklist_items: nextChecklist,
          header_image_url: nextHeader,
        });
      }

      setPendingHeaderFile(null);
      setPendingChecklistFiles({});
      setEditing(null);
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Steps (2026-10-25): remove one (and its picture waiting to upload), or move it up/down.
  const removeStep = (id: string) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const left = prev.checklist.filter((c) => c.id !== id);
      return { ...prev, checklist: left.length ? left : [{ id: newChecklistId(), text: '' }] };
    });
    setPendingChecklistFiles((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const moveStep = (from: number, to: number) => {
    setEditing((prev) => (prev ? { ...prev, checklist: moveItem(prev.checklist, from, to) } : prev));
  };

  const translate = async () => {
    if (!editing?.group_id) return;
    setTranslating(true);
    setErr('');
    try {
      const { failed } = await catalogTranslateTask(editing.group_id);
      if (failed.length > 0) setErr(`Translation failed for: ${failed.join(', ')}. Try again.`);
      refreshAnthropicUsage();
      const groupsNow = await catalogTasksList();
      setGroups(groupsNow);
      const g = groupsNow.find((x) => x.id === editing.group_id);
      if (g) {
        const t = g.task_templates?.find((x) => x.language_code === editing.language_code);
        setEditing(mergeTemplate(g, editing.language_code, t));
      }
    } catch (e: any) {
      setErr(e?.message || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const markReviewed = async () => {
    if (!editing?.template_id) return;
    setMarkingReviewed(true);
    setErr('');
    try {
      await catalogMarkTaskReviewed(editing.template_id);
      const groupsNow = await catalogTasksList();
      setGroups(groupsNow);
      const g = groupsNow.find((x) => x.id === editing.group_id);
      if (g) {
        const t = g.task_templates?.find((x) => x.language_code === editing.language_code);
        setEditing(mergeTemplate(g, editing.language_code, t));
      }
    } catch (e: any) {
      setErr(e?.message || 'Failed to mark reviewed');
    } finally {
      setMarkingReviewed(false);
    }
  };

  const deactivate = async (group: CatalogTaskGroupRow) => {
    if (!window.confirm(`Deactivate standard task "${group.group_key || group.id}"? It will disappear from family task libraries.`)) return;
    setErr('');
    try {
      await catalogTasksSoftDelete({ group_id: group.id });
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Deactivate failed');
    }
  };

  const displayTitle = (g: CatalogTaskGroupRow) => {
    const templates = Array.isArray(g.task_templates) ? g.task_templates : [];
    const t = templates.find((x) => x.language_code === 'en') || templates[0];
    return t?.title || g.group_key || g.id;
  };

  const needsReviewLangsForGroup = (g: CatalogTaskGroupRow): string[] => {
    const templates = Array.isArray(g.task_templates) ? g.task_templates : [];
    return templates.filter((t) => t.needs_review).map((t) => t.language_code);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Standard tasks</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
          Families see these in the Task library when creating tasks (in their selected language). Tool keys reference
          the global tool catalog; they are matched to each family&apos;s tools by <code className="text-xs">tool_key</code>.
        </p>
      </div>

      {bulk && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3 rounded-lg text-sm">
          {bulk.total === 0
            ? 'Every active standard task already has all languages.'
            : bulk.done < bulk.total
              ? `Translating… ${bulk.done} of ${bulk.total} tasks done. Keep this page open.`
              : `Done: ${bulk.total} tasks translated (marked “needs review”).`}
          {bulk.failed.length > 0 && <div className="mt-1 text-red-700 dark:text-red-300">Failed: {bulk.failed.join('; ')}</div>}
        </div>
      )}

      {err && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {err}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="btn btn-primary" onClick={openNew}>
            Add standard task
          </button>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
            Refresh
          </button>
          {bulk && bulk.done < bulk.total ? (
            <button type="button" className="btn btn-secondary" onClick={() => { bulkStop.current = true; }}>
              Stop translating ({bulk.done}/{bulk.total})
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={loading || translating}
              onClick={async () => {
                const all = supported_languages.map((l) => l.code);
                const todo = groups.filter((g) => g.is_active !== false).filter((g) => {
                  const have = new Set((g.task_templates || []).map((t) => t.language_code));
                  return have.has('en') && all.some((c) => !have.has(c));
                });
                if (todo.length === 0) { setBulk({ done: 0, total: 0, failed: [] }); return; }
                if (!window.confirm(`Translate ${todo.length} standard tasks into the missing languages? This calls Claude once per task and language.`)) return;
                bulkStop.current = false;
                const failed: string[] = [];
                setBulk({ done: 0, total: todo.length, failed });
                for (let i = 0; i < todo.length; i++) {
                  if (bulkStop.current) break;
                  const g = todo[i];
                  const have = new Set((g.task_templates || []).map((t) => t.language_code));
                  try {
                    const res = await catalogTranslateTask(g.id, all.filter((c) => c !== 'en' && !have.has(c)));
                    if (res.failed?.length) failed.push(`${g.group_key} (${res.failed.join(', ')})`);
                  } catch (e: any) {
                    failed.push(`${g.group_key}: ${e?.message || 'error'}`);
                  }
                  setBulk({ done: i + 1, total: todo.length, failed: [...failed] });
                }
                await load();
              }}
            >
              Translate all missing
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input type="checkbox" checked={onlyNeedsReview} onChange={(e) => setOnlyNeedsReview(e.target.checked)} />
          Show only items needing review
        </label>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/80">
              <tr>
                <th className="text-left p-3 font-medium">Key</th>
                <th className="text-left p-3 font-medium">Title (preview)</th>
                <th className="text-left p-3 font-medium">Points</th>
                <th className="text-left p-3 font-medium">Languages</th>
                <th className="text-left p-3 font-medium">Needs review</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups
                .filter((g) => !onlyNeedsReview || needsReviewLangsForGroup(g).length > 0)
                .map((g) => {
                  const templates = Array.isArray(g.task_templates) ? g.task_templates : [];
                  const langs = Array.from(new Set(templates.map((x) => x.language_code)))
                    .sort()
                    .join(', ');
                  const t0 = templates.find((x) => x.language_code === 'en') || templates[0];
                  const pending = needsReviewLangsForGroup(g);
                  return (
                    <tr key={g.id} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="p-3 font-mono text-xs">{g.group_key || '—'}</td>
                      <td className="p-3">{displayTitle(g)}</td>
                      <td className="p-3">{t0?.points ?? '—'}</td>
                      <td className="p-3 text-gray-600 dark:text-gray-400">{langs || '—'}</td>
                      <td className="p-3">
                        {pending.length > 0 ? (
                          <span
                            className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-medium"
                            title={pending.join(', ')}
                          >
                            {pending.length} lang{pending.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="p-3 text-right space-x-2">
                        <button type="button" className="btn btn-info btn-small" onClick={() => openEdit(g)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-danger btn-small" onClick={() => deactivate(g)}>
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {groups.length === 0 && <p className="p-6 text-center text-gray-500">No standard tasks yet.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editing.group_id ? 'Edit standard task' : 'New standard task'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {!editing.group_id && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Task key (slug)</label>
                    <input
                      className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                      value={editing.group_key}
                      onChange={(e) => setEditing({ ...editing, group_key: e.target.value })}
                      placeholder="e.g. vacuum_living_room"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Sort order</label>
                    <input
                      type="number"
                      className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                      value={editing.sort_order}
                      onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                    />
                  </div>
                </div>
              )}

              {editing.group_id && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Task key</label>
                    <input
                      className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/50"
                      value={editing.group_key}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Sort order</label>
                    <input
                      type="number"
                      className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                      value={editing.sort_order}
                      onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                    <span>Language</span>
                    {editing.needs_review && (
                      <>
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                          needs review
                        </span>
                        <button
                          type="button"
                          className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                          onClick={markReviewed}
                          disabled={markingReviewed}
                        >
                          {markingReviewed ? 'Marking…' : 'Mark reviewed'}
                        </button>
                      </>
                    )}
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                    value={editing.language_code}
                    onChange={(e) => {
                      const lang = e.target.value;
                      if (!editing.group_id) {
                        setEditing({ ...editing, language_code: lang });
                        return;
                      }
                      const g = groups.find((x) => x.id === editing.group_id);
                      if (g) onLanguageChange(g, lang);
                    }}
                  >
                    {supported_languages.map(({ code, label }) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Switch language to edit another locale for the same task key, or start from empty fields to add a new
                    language.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Age group</label>
                  <select
                    className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                    value={editing.age_group}
                    onChange={(e) => setEditing({ ...editing, age_group: e.target.value as AgeGroupCode })}
                  >
                    <option value="all_ages">All ages</option>
                    <option value="age_2_3">2–3 years</option>
                    <option value="age_4_5">4–5 years</option>
                    <option value="age_6_7">6–7 years</option>
                    <option value="age_8_9">8–9 years</option>
                    <option value="age_10_12">10–12 years</option>
                    <option value="age_13_plus">13+ years</option>
                    <option value="adults">Grown-ups</option>
                  </select>
                </div>
              </div>

              {editing.group_id && (
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={translate}
                    disabled={translating || saving}
                    title="Auto-translate the English title/description/checklist into all other languages via Claude"
                  >
                    {translating ? 'Translating…' : 'Translate to all languages'}
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                  rows={2}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Header image (optional)</label>
                <div className="flex flex-wrap gap-3 items-start">
                  {(headerPreviewUrl || (!pendingHeaderFile && editing.header_image_url)) && (
                    <img
                      src={headerPreviewUrl || editing.header_image_url}
                      alt=""
                      className="h-20 w-auto max-w-[200px] rounded border border-gray-200 dark:border-gray-600 object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-[200px] space-y-2">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/svg+xml"
                      className="block w-full text-sm"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        setPendingHeaderFile(f ?? null);
                        e.target.value = '';
                      }}
                    />
                    <input
                      type="url"
                      className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                      placeholder="Or paste https://…"
                      value={editing.header_image_url}
                      onChange={(e) => {
                        setPendingHeaderFile(null);
                        setEditing({ ...editing, header_image_url: e.target.value });
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Upload stores the file in the catalog bucket. When a family uses this task, the app copies it to their
                  folder so edits stay private to that family.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Points</label>
                  <input
                    type="number"
                    min={1}
                    className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                    value={editing.points}
                    onChange={(e) => setEditing({ ...editing, points: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Suggested room (match)</label>
                  <select
                    className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                    value={editing.suggested_room_original_name}
                    onChange={(e) => setEditing({ ...editing, suggested_room_original_name: e.target.value })}
                  >
                    {roomOptions.map((r) => (
                      <option key={r.value || 'none'} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Same repeat editor as parents use (2026-10-15); a plan here has no start date. */}
              <div className="rounded border p-3 dark:border-gray-600">
                <RepeatSettings
                  forTemplate
                  data={{
                    recreate_on_validation: editing.recreate_on_validation,
                    recurrence_days: editing.recurrence_days,
                    days_to_complete: editing.days_to_complete === '' ? 0 : Number(editing.days_to_complete),
                    repeat_schedule: editing.repeat_schedule,
                    auto_available: editing.auto_available,
                  }}
                  members={[]}
                  nextChoices={[]}
                  onChange={(p: any) => setEditing((prev) => prev && ({
                    ...prev,
                    ...('recreate_on_validation' in p ? { recreate_on_validation: !!p.recreate_on_validation } : {}),
                    ...('recurrence_days' in p ? { recurrence_days: Number(p.recurrence_days) || 0 } : {}),
                    ...('days_to_complete' in p ? { days_to_complete: Number(p.days_to_complete) > 0 ? Number(p.days_to_complete) : '' } : {}),
                    ...('repeat_schedule' in p ? { repeat_schedule: p.repeat_schedule ?? null } : {}),
                  }))}
                />
              </div>

              <fieldset className="rounded border p-3 dark:border-gray-600 space-y-2">
                <legend className="text-sm font-medium px-1">New family setup</legend>
                <select
                  className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                  value={editing.starter}
                  onChange={(e) => setEditing({ ...editing, starter: e.target.value as EditForm['starter'] })}
                >
                  <option value="">Not suggested in setup</option>
                  <option value="per_child">Suggested for each child's own room (personal task)</option>
                  <option value="shared">Suggested for the room it belongs to (open to anyone)</option>
                </select>
                {editing.starter && (
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={editing.starter_checked}
                      onChange={(e) => setEditing({ ...editing, starter_checked: e.target.checked })} />
                    <span className="text-sm">Ticked by default (parents can untick it)</span>
                  </label>
                )}
                <p className="text-xs text-gray-500">
                  Shown only when the family chose the room and a child is old enough (age group). Changes here apply to
                  future setups and future "Use" — never to tasks families already have.
                </p>
              </fieldset>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.auto_available}
                  onChange={(e) => setEditing({ ...editing, auto_available: e.target.checked })}
                />
                <span className="text-sm">Automatically available (for recurring)</span>
              </label>

              <div>
                <label className="block text-sm font-medium mb-2">Checklist</label>
                <div className="space-y-3">
                  {editing.checklist.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex flex-wrap gap-2 items-start border border-gray-100 dark:border-gray-700 rounded-lg p-2"
                    >
                      {/* Move / remove a step (2026-10-25, Jens: updating standard tasks). */}
                      <div className="basis-full flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300 mr-auto">Step {index + 1}</span>
                        <button type="button" className="px-2 py-1 rounded border dark:border-gray-600 text-sm disabled:opacity-30"
                          aria-label={`Move step ${index + 1} up`} disabled={index === 0}
                          onClick={() => moveStep(index, index - 1)}>↑</button>
                        <button type="button" className="px-2 py-1 rounded border dark:border-gray-600 text-sm disabled:opacity-30"
                          aria-label={`Move step ${index + 1} down`} disabled={index === editing.checklist.length - 1}
                          onClick={() => moveStep(index, index + 1)}>↓</button>
                        <button type="button" className="px-2 py-1 rounded border border-red-300 dark:border-red-800 text-sm text-red-700 dark:text-red-300"
                          aria-label={`Remove step ${index + 1}`}
                          onClick={() => removeStep(item.id)}>✕ Remove</button>
                      </div>
                      <input
                        className="flex-1 min-w-[160px] border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
                        value={item.text}
                        placeholder="Checklist step"
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            checklist: editing.checklist.map((c) =>
                              c.id === item.id ? { ...c, text: e.target.value } : c,
                            ),
                          })
                        }
                      />
                      <div className="flex items-center gap-2 shrink-0">
                        {(checklistPreviewUrls[item.id] || (!pendingChecklistFiles[item.id] && item.image_url)) && (
                          <img
                            src={checklistPreviewUrls[item.id] || item.image_url}
                            alt=""
                            className="h-12 w-12 rounded object-cover border border-gray-200 dark:border-gray-600"
                          />
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/svg+xml"
                          className="text-xs max-w-[140px]"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setPendingChecklistFiles((prev) => {
                              const next = { ...prev };
                              if (f) next[item.id] = f;
                              else delete next[item.id];
                              return next;
                            });
                            e.target.value = '';
                          }}
                        />
                      </div>
                      {/* Timed step (2026-10-19): the same timer in every language (copied on translate). */}
                      <div className="basis-full">
                        <StepTimerInput
                          seconds={item.timer_seconds}
                          onChange={(sec) => setEditing((prev) => prev && ({
                            ...prev,
                            checklist: prev.checklist.map((c) => (c.id === item.id ? { ...c, timer_seconds: sec } : c)),
                          }))}
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="px-3 py-2 rounded border border-blue-300 dark:border-blue-700 text-sm font-medium text-blue-700 dark:text-blue-300"
                    onClick={() =>
                      setEditing({
                        ...editing,
                        checklist: [...editing.checklist, { id: newChecklistId(), text: '' }],
                      })
                    }
                  >
                    + Add step
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tools (by catalog key)</label>
                <div className="max-h-40 overflow-y-auto border rounded p-2 dark:border-gray-600 space-y-1">
                  {catalogToolKeys.length === 0 ? (
                    <p className="text-xs text-gray-500">No tools in global catalog.</p>
                  ) : (
                    catalogToolKeys.map((t) => (
                      <label key={t.tool_key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={editing.tool_keys.includes(t.tool_key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditing({ ...editing, tool_keys: [...editing.tool_keys, t.tool_key] });
                            } else {
                              setEditing({
                                ...editing,
                                tool_keys: editing.tool_keys.filter((k) => k !== t.tool_key),
                              });
                            }
                          }}
                        />
                        <span>
                          {t.name} <span className="text-gray-400 font-mono text-xs">({t.tool_key})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalCatalogTasks;
